import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import { FaDesktop, FaDownload, FaRedo, FaCrown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Context } from "../../index";
import { createMyRdpRequest, downloadMyRdpFile, fetchMyRdpAccess } from "../../http/rdpAPI";
import { PAY_ROUTE } from "../../utils/consts";
import rdpShortcutIcon from "../../assets/rdp-shortcut.png";

const PREMIUM_PLANS = new Set(["PREMIUM", "MAXIMUM"]);

const badgeVariant = (status) => {
    switch (status) {
        case "ACTIVE":
        case "APPROVED":
            return "success";
        case "PENDING":
        case "MANUAL_REQUIRED":
            return "warning";
        case "REJECTED":
        case "DISABLED":
            return "secondary";
        case "DELETED":
            return "danger";
        default:
            return "light";
    }
};

const statusText = (request, account) => {
    if (account?.status === "ACTIVE") return "Ready to connect";
    if (account?.status === "MANUAL_REQUIRED") return "Credentials prepared";
    if (account?.status === "DISABLED") return "Access disabled";
    if (account?.status === "DELETED") return "Access deleted";
    if (request?.status === "PENDING") return "Request pending";
    if (request?.status === "REJECTED") return "Request rejected";
    return "Premium remote workspace";
};

const ColRdpAccess = ({ onClose }) => {
    const { user } = useContext(Context);
    const navigate = useNavigate();
    const plan = String(user?.user?.tariff_plan || "").toUpperCase();
    const isEligible = PREMIUM_PLANS.has(plan);

    const [access, setAccess] = useState(null);
    const [loading, setLoading] = useState(isEligible);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [optimisticRequest, setOptimisticRequest] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const [passwordCopied, setPasswordCopied] = useState(false);

    const rawRequest = optimisticRequest || access?.request || null;
    const rawAccount = access?.account || null;
    const isDeletedAccess = rawAccount?.status === "DELETED";
    const request = rawRequest;
    const account = isDeletedAccess ? null : rawAccount;
    const server = account?.server || null;
    const isReady = account && ["MANUAL_REQUIRED", "ACTIVE"].includes(account.status);
    const isBlockingRequest = request && !["REJECTED", "CANCELED"].includes(request.status);
    const currentStatus = account?.status || request?.status || "AVAILABLE";
    const requestLabel = request?.id ? `Request #${request.id}` : "Request";

    const subText = useMemo(() => statusText(request, account), [request, account]);

    const load = useCallback(async () => {
        if (!isEligible) return;
        setLoading(true);
        setError("");
        try {
            const data = await fetchMyRdpAccess();
            setAccess(data);
            if (data?.request || data?.account) {
                setOptimisticRequest(null);
                setSuccess("");
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message || "Failed to load RDP access.");
        } finally {
            setLoading(false);
        }
    }, [isEligible]);

    useEffect(() => {
        load();
    }, [load, user?.user?.id]);

    const orderAccess = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const data = await createMyRdpRequest();
            const nextRequest = data?.request || { id: null, status: "PENDING" };
            setOptimisticRequest(nextRequest);
            setAccess((prev) => ({ ...(prev || {}), request: nextRequest }));
            await load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message || "Failed to request RDP access.");
        } finally {
            setSaving(false);
        }
    };

    const downloadRdp = async () => {
        setSaving(true);
        setError("");
        try {
            const { blob, filename } = await downloadMyRdpFile();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename || `virtalibry-rdp-${account?.windows_username || "workspace"}.rdp`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            if (onClose) onClose();
        } catch (e) {
            setError(e?.response?.data?.message || e.message || "RDP file is not ready yet.");
        } finally {
            setSaving(false);
        }
    };

    const copyPassword = async () => {
        if (!account?.windows_password) return;

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(account.windows_password);
            } else {
                const input = document.createElement("textarea");
                input.value = account.windows_password;
                input.setAttribute("readonly", "readonly");
                input.style.position = "fixed";
                input.style.opacity = "0";
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                input.remove();
            }
            setPasswordCopied(true);
            setTimeout(() => setPasswordCopied(false), 1300);
        } catch (e) {
            setError("Could not copy password. Please copy it manually.");
        }
    };

    return (
        <Card className="p-3 py-2 w-100 mt-2">
            <div className="d-flex flex-column flex-md-row flex-md-nowrap justify-content-between align-items-start align-items-md-center gap-2">
                <h5 className="text-muted mb-0 me-md-3 text-nowrap">
                    Shared Windows RDP <span className="d-none d-md-inline">(Test extra-bonus service)</span>
                </h5>
                <div className="d-flex flex-wrap justify-content-start justify-content-md-end gap-2">
                    {!isEligible ? (
                        <Button variant="outline-success" size="sm" onClick={() => navigate(PAY_ROUTE)}>
                            <FaCrown className="me-2" />
                            Upgrade plan
                        </Button>
                    ) : (
                        <>
                            {!account && (
                                <Button
                                    variant={isBlockingRequest ? "outline-secondary" : "outline-success"}
                                    size="sm"
                                    onClick={orderAccess}
                                    disabled={saving || loading || isBlockingRequest}
                                >
                                    {saving ? <Spinner size="sm" className="me-2" /> : <FaDesktop className="me-2" />}
                                    {isBlockingRequest ? "Already sent" : "Order access"}
                                </Button>
                            )}

                            {isReady && (
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={downloadRdp}
                                    disabled={saving}
                                >
                                    <FaDownload className="me-2" />
                                    RDP file
                                </Button>
                            )}

                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={load}
                                disabled={saving || loading}
                            >
                                {loading ? <Spinner size="sm" className="me-2" /> : <FaRedo className="me-2" />}
                                Refresh
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="mt-2">
                <div className="row g-3">
                    <div className="col-12 col-lg-6">
                        <Card className="h-100 border-secondary">
                        <div style={{ position: "relative" }}>
                        <Card.Header className="py-1 d-flex justify-content-between align-items-center">
                            <span>Premium remote workspace</span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                style={{ width: "20px", height: "20px", lineHeight: "1" }}
                                onClick={() => setShowHelp(!showHelp)}
                            >
                                <strong>?</strong>
                            </Button>
                        </Card.Header>
                        {showHelp && (
                            <div
                                className="border border-secondary"
                                style={{
                                    position: "absolute",
                                    bottom: "32px",
                                    right: "0",
                                    background: "white",
                                    border: "1px solid #ccc",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    zIndex: 100,
                                    boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                    width: "360px",
                                    maxWidth: "calc(100vw - 48px)",
                                }}
                            >
                                <p style={{ fontSize: "14px", marginBottom: "10px" }}>
                                    <strong>Shared Windows RDP</strong><br />
                                    This is a prepared remote Windows workspace for Amazon and Virtalibry work.
                                    "Shared" means that multiple users work on the same Windows Server, but each
                                    approved user gets a separated Windows account and their own assigned unique
                                    proxy IP.
                                    It works similarly to a VPN: your browser traffic goes through an assigned
                                    clean proxy IP, and that IP is the address the destination website sees.
                                    Each approved workspace receives its own Windows login, assigned proxy and
                                    downloadable RDP file. This is a test extra-bonus service, so please send
                                    wishes and feedback instead of strict service requirements.
                                </p>
                                <div className="d-flex justify-content-end">
                                    <button onClick={() => setShowHelp(false)} className="btn btn-outline-success btn-sm">
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                        </div>
                        <Card.Body className="p-2 small">
                            {!isEligible ? (
                                <div className="text-muted">
                                    Upgrade to Premium or Maximum and get a prepared shared Windows RDP workspace
                                    for Amazon and Virtalibry work. This is a test extra-bonus service, so we
                                    welcome wishes and feedback while it is being refined.
                                </div>
                            ) : loading ? (
                                <div className="d-flex align-items-center gap-2 text-muted">
                                    <Spinner size="sm" />
                                    Loading RDP access...
                                </div>
                            ) : isReady ? (
                                <div>
                                    <div className="d-flex align-items-center justify-content-between gap-3">
                                        <div className="row g-2 align-items-start flex-grow-1">
                                            <div className="col-12 col-md-4">
                                                <div className="text-muted">Server</div>
                                                <div className="fw-semibold text-break">{server ? `${server.host}:${server.port || 3389}` : "Preparing"}</div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="text-muted">Windows Login</div>
                                                <div className="fw-semibold text-break">{account.windows_username}</div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="text-muted">Password</div>
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 fw-semibold text-dark text-decoration-none text-break"
                                                    style={{ fontSize: "inherit", lineHeight: "inherit", cursor: "copy" }}
                                                    title="Click to copy password"
                                                    onClick={copyPassword}
                                                >
                                                    {passwordCopied ? "Copied" : account.windows_password}
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            className="text-center flex-shrink-0 d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "46px",
                                                height: "46px",
                                                borderRadius: "50%",
                                                background: "#198754",
                                                border: "1px solid #146c43",
                                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25)",
                                                overflow: "hidden",
                                            }}
                                            title={account.windows_username}
                                        >
                                            <img
                                                src={rdpShortcutIcon}
                                                alt=""
                                                className="d-block"
                                                style={{
                                                    width: "34px",
                                                    height: "auto",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : isBlockingRequest ? (
                                <div className="text-muted">
                                    {requestLabel} is {String(request.status || "").toLowerCase()}. Admin will prepare your Windows account, assigned proxy and RDP file.
                                    This is a test extra-bonus service, so wishes and feedback are welcome.
                                </div>
                            ) : (
                                <div className="text-muted">
                                    Request shared RDP access. Admin will prepare your Windows account, assigned proxy and RDP file.
                                    This is a test extra-bonus service, so wishes and feedback are welcome.
                                </div>
                            )}
                        </Card.Body>
                        </Card>
                    </div>

                    <div className="col-12 col-lg-6">
                        <Card className="h-100 border-secondary">
                        <Card.Header className="py-1 d-flex align-items-center justify-content-between">
                            <span>Status of Shared RDP</span>
                            {isEligible ? (
                                <Badge bg={badgeVariant(currentStatus)}>{currentStatus}</Badge>
                            ) : (
                                <Badge bg="secondary">PREMIUM / MAXIMUM</Badge>
                            )}
                        </Card.Header>
                        <Card.Body className="p-2 small">
                            {error ? (
                                <Alert variant="danger" className="py-2 px-2 mb-0">
                                    {error}
                                </Alert>
                            ) : !isEligible ? (
                                <div className="text-muted">
                                    Current plan: {plan || "FREE"}. Shared RDP is included in Premium and Maximum plans.
                                </div>
                            ) : request?.status === "PENDING" && !account ? (
                                <div className="text-success">
                                    {requestLabel} is waiting for manual admin approval.
                                </div>
                            ) : request?.status === "APPROVED" && !account ? (
                                <div className="text-success">
                                    {requestLabel} is approved. Your RDP workspace is being prepared.
                                </div>
                            ) : success ? (
                                <div className="text-success">
                                    {success}
                                </div>
                            ) : request?.status === "REJECTED" && !account ? (
                                <div className="text-muted">
                                    Your request was rejected. Contact support if this looks wrong.
                                </div>
                            ) : isReady ? (
                                <div className="text-muted">
                                    {subText}.{" "}
                                    <Button
                                        variant="link"
                                        className="p-0 align-baseline text-success"
                                        style={{ fontSize: "inherit", lineHeight: "inherit" }}
                                        onClick={downloadRdp}
                                        disabled={saving}
                                    >
                                        Download RDP file
                                    </Button>{" "}
                                    and open downloaded file to connect. Please send wishes and feedback.
                                </div>
                            ) : (
                                <div className="text-muted">
                                    No active request yet. Click Order access to start provisioning.
                                </div>
                            )}
                        </Card.Body>
                        </Card>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default ColRdpAccess;
